import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Plus, Trash2, Copy, Users, Link2, DollarSign, 
  CheckCircle, TrendingUp, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeesPage = () => {
  const { isOwner } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [incentives, setIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    email: '',
    phone: '',
    hire_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, incentivesRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/referrals/incentives`)
      ]);
      setEmployees(employeesRes.data);
      setIncentives(incentivesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const createEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employees`, newEmployee);
      toast.success('Employee added successfully');
      setDialogOpen(false);
      setNewEmployee({ name: '', email: '', phone: '', hire_date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    }
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm('Are you sure you want to remove this employee?')) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success('Employee removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove employee');
    }
  };

  const copyReferralLink = (code) => {
    const link = `${window.location.origin}/?ref=${code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied to clipboard!');
  };

  const markIncentivePaid = async (id) => {
    try {
      await axios.post(`${API}/referrals/incentives/${id}/mark-paid`);
      toast.success('Incentive marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to update incentive');
    }
  };

  const totalReferrals = employees.reduce((sum, e) => sum + (e.referral_stats?.total || 0), 0);
  const totalHired = employees.reduce((sum, e) => sum + (e.referral_stats?.hired || 0), 0);
  const pendingIncentives = incentives.filter(i => !i.paid).length;

  return (
    <div className="p-6 space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#4B2D6E] font-[Lato]">Employee Management</h1>
          <p className="text-[#666666] mt-1">Manage employees and referral program</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" className="border-[#4B2D6E] text-[#4B2D6E]">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {isOwner && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#E8722A] hover:bg-[#D6621F]" data-testid="add-employee-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Employee
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Employee</DialogTitle>
                </DialogHeader>
                <form onSubmit={createEmployee} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={newEmployee.name}
                      onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                      required
                      data-testid="employee-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newEmployee.email}
                      onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                      required
                      data-testid="employee-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={newEmployee.phone}
                      onChange={(e) => setNewEmployee({ ...newEmployee, phone: e.target.value })}
                      required
                      data-testid="employee-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hire_date">Hire Date</Label>
                    <Input
                      id="hire_date"
                      type="date"
                      value={newEmployee.hire_date}
                      onChange={(e) => setNewEmployee({ ...newEmployee, hire_date: e.target.value })}
                      required
                      data-testid="employee-date-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#E8722A] hover:bg-[#D6621F]" data-testid="save-employee-btn">
                    Add Employee
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#4B2D6E] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Users className="h-8 w-8" />
              <div>
                <p className="text-sm text-gray-200">Active Employees</p>
                <p className="text-3xl font-bold">{employees.filter(e => e.status === 'active').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#E8722A] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Link2 className="h-8 w-8" />
              <div>
                <p className="text-sm text-gray-100">Total Referrals</p>
                <p className="text-3xl font-bold">{totalReferrals}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#4CAF50] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <TrendingUp className="h-8 w-8" />
              <div>
                <p className="text-sm text-gray-100">Referrals Hired</p>
                <p className="text-3xl font-bold">{totalHired}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#1976D2] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <DollarSign className="h-8 w-8" />
              <div>
                <p className="text-sm text-gray-100">Pending Incentives</p>
                <p className="text-3xl font-bold">{pendingIncentives}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <Tabs defaultValue="roster">
          <CardHeader className="pb-0">
            <TabsList>
              <TabsTrigger value="roster">Employee Roster</TabsTrigger>
              <TabsTrigger value="incentives">Referral Incentives</TabsTrigger>
            </TabsList>
          </CardHeader>

          {/* Roster Tab */}
          <TabsContent value="roster">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#4B2D6E] border-t-transparent mx-auto"></div>
                </div>
              ) : employees.length === 0 ? (
                <div className="p-12 text-center text-[#666666]">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No employees yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F5F5]">
                        <TableHead>Employee</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Hire Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Referrals</TableHead>
                        <TableHead>Referral Link</TableHead>
                        {isOwner && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employees.map((employee) => (
                        <TableRow key={employee.id} data-testid={`employee-row-${employee.id}`}>
                          <TableCell className="font-medium">{employee.name}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p>{employee.email}</p>
                              <p className="text-[#666666]">{employee.phone}</p>
                            </div>
                          </TableCell>
                          <TableCell>{employee.hire_date}</TableCell>
                          <TableCell>
                            <Badge className={employee.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {employee.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p>{employee.referral_stats?.total || 0} referred</p>
                              <p className="text-[#4CAF50]">{employee.referral_stats?.hired || 0} hired</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyReferralLink(employee.referral_code)}
                              className="text-[#4B2D6E]"
                              data-testid={`copy-link-${employee.id}`}
                            >
                              <Copy className="mr-1 h-3 w-3" />
                              {employee.referral_code}
                            </Button>
                          </TableCell>
                          {isOwner && (
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteEmployee(employee.id)}
                                data-testid={`delete-employee-${employee.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </TabsContent>

          {/* Incentives Tab */}
          <TabsContent value="incentives">
            <CardContent>
              {incentives.length === 0 ? (
                <div className="text-center py-12 text-[#666666]">
                  <DollarSign className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No incentives to display</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F5F5F5]">
                        <TableHead>Employee</TableHead>
                        <TableHead>Referred Candidate</TableHead>
                        <TableHead>Trigger</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        {isOwner && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {incentives.map((incentive) => (
                        <TableRow key={incentive.id}>
                          <TableCell className="font-medium">{incentive.employee_name}</TableCell>
                          <TableCell>{incentive.candidate_name}</TableCell>
                          <TableCell className="capitalize">{incentive.trigger}</TableCell>
                          <TableCell>${incentive.amount}</TableCell>
                          <TableCell>
                            {incentive.paid ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="mr-1 h-3 w-3" /> Paid
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                            )}
                          </TableCell>
                          {isOwner && (
                            <TableCell className="text-right">
                              {!incentive.paid && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => markIncentivePaid(incentive.id)}
                                  className="text-green-600 border-green-600"
                                >
                                  Mark Paid
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default EmployeesPage;
